import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Users, 
  Shield, 
  UserCheck, 
  Settings,
  Key,
  Lock,
  UserPlus,
  Crown
} from 'lucide-react';
import Layout from '../components/Layout';

// Import des composants utilisateurs
import {
  UtilisateurTab,
  RoleTab
} from '../components/utilisateurs';

const UtilisateurPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('utilisateurs');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 500);
    return () => clearTimeout(timer);
  }, []);

  const tabs = [
    {
      id: 'utilisateurs',
      label: 'Utilisateurs',
      icon: Users,
      component: UtilisateurTab
    },
    {
      id: 'roles',
      label: 'Rôles',
      icon: Shield,
      component: RoleTab
    }
  ];

  const ActiveComponent = tabs.find(tab => tab.id === activeTab)?.component;

  if (loading) {
    return (
      <Layout>
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#202654]"></div>
            <span className="ml-3 text-gray-600">Chargement du module utilisateurs...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
          
          {/* En-tête professionnel */}
          <div className="bg-gradient-to-r from-[#202654] to-[#202654]/90 px-6 py-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between">
              <div className="flex items-center text-white">
                <button
                  onClick={() => navigate(-1)}
                  className="mr-4 p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <div className="bg-white/10 p-3 rounded-xl mr-4">
                  <UserCheck className="h-8 w-8 text-white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold">Gestion des Utilisateurs</h1>
                  <p className="mt-1 text-blue-100">Administration des comptes et des permissions</p>
                </div>
              </div>
              
              <div className="mt-4 md:mt-0 flex flex-col sm:flex-row sm:space-x-3 space-y-3 sm:space-y-0">
              <div className="hidden md:flex items-center space-x-6 text-white/80">
                <div className="flex items-center">
                  <Lock className="h-5 w-5 mr-2" />
                  <span className="text-sm">Sécurisé</span>
                </div>
                <div className="flex items-center">
                  <Key className="h-5 w-5 mr-2" />
                  <span className="text-sm">Contrôlé</span>
                </div>
                <div className="flex items-center">
                  <Crown className="h-5 w-5 mr-2" />
                  <span className="text-sm">Administré</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="mt-4 flex items-center justify-between">
              <div className="flex items-center text-green-100">
                <div className="w-2 h-2 bg-green-400 rounded-full mr-2 animate-pulse"></div>
                <span className="text-sm">Système opérationnel</span>
              </div>
              <div className="text-white/70 text-sm">
                Dernière synchronisation: {new Date().toLocaleTimeString('fr-FR')}
              </div>
            </div>
          </div>

          {/* Navigation Tabs */}
          <div className="bg-gray-50 border-b border-gray-200">
            <div className="px-8">
              <nav className="flex space-x-1 overflow-x-auto py-2">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`group flex flex-col items-center px-6 py-3 text-sm font-medium rounded-lg transition-all duration-200 ease-in-out whitespace-nowrap ${
                        activeTab === tab.id
                          ? 'bg-[#202654] text-white shadow-md transform scale-105'
                          : 'text-gray-600 hover:text-[#202654] hover:bg-white hover:shadow-sm'
                      }`}
                    >
                      <Icon className={`h-5 w-5 mb-1 ${
                        activeTab === tab.id ? 'text-white' : 'text-gray-400 group-hover:text-[#202654]'
                      }`} />
                      <span className="text-xs">{tab.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Contenu principal */}
          <div className="p-6 bg-gray-50">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              {ActiveComponent && <ActiveComponent />}
            </div>
          </div>

        </div>
      </div>
    </Layout>
  );
};

export default UtilisateurPage;
